import { API_BASE_URL } from "@/config";
import { validateToken } from "@/utils/auth";
import CustomTable from "@/components/CustomTable/CustomTable.vue";
import ProgressBarComponent from "@/components/ProgressBar/ProgressBarComponent.vue";
import IconCard from "@/components/CustomCard/IconCard.vue";
import SlideTabComponent from "@/components/SlideTab/SlideTabComponent.vue";
import { format_date } from "@/utils/date_formatter";

// Calling reports for data visualization.
import CompanyCreditReport from '@/layouts/pdf_reporting/company_credit_report/CompanyCreditReport.vue';
import AMLReport from '@/layouts/pdf_reporting/aml_reporting/AMLReport.vue';
import ReportViewer from '@/components/v2_report/ReportViewer.vue';
import RefugeeVerificationReport from '@/layouts/pdf_reporting/refugee_verification/RefugeeVerificationReport.vue';
import EnterpriseAmlVerificationReport from '@/layouts/pdf_reporting/enterprise_aml/EnterpriseAmlVerificationReport.vue';
import RsaIdVerificationReport from "@/layouts/pdf_reporting/rsa_id_verification/RsaIdVerificationReport.vue";
import DirectorScreeningReport from "@/layouts/pdf_reporting/director_screening/DirectorScreeningReport.vue";
import TrustSearchReport from "@/layouts/pdf_reporting/trust_search_report/TrustSearchReport.vue";
import CIPCReport from "@/layouts/pdf_reporting/cipc_reporting/CIPCReport.vue";
import CompanyBankAccountReport from '@/layouts/pdf_reporting/bank_account_verification/company_bank_account/CompanyBankAccountReport.vue';
import IndividualBankAccountReport from '@/layouts/pdf_reporting/bank_account_verification/individual_bank_account/IndividualBankAccountReport.vue';
import EnterpriseKYBReport from '@/layouts/pdf_reporting/enterprise_kyb/EnterpriseKYBReport.vue';


export default {
  name: "ViewLogs",
  components: {
    CustomTable,
    IconCard,
    ProgressBarComponent,
    SlideTabComponent,
    // Reports
    CompanyCreditReport,
    AMLReport,
    ReportViewer,
    RefugeeVerificationReport,
    EnterpriseAmlVerificationReport,
    RsaIdVerificationReport,
    DirectorScreeningReport,
    TrustSearchReport,
    CIPCReport,
    CompanyBankAccountReport,
    IndividualBankAccountReport,
    EnterpriseKYBReport
  },

  data: () => ({
    loading: false,
    logs: [],
    analytical_data: null,
    top_services: [],
    services_list: [],
    service_id_map: {}, // Map service names to service IDs
    response_services_list: [], // Services list for API responses
    response_service_id_map: {}, // Service ID map for API responses
    status_options: ['Succeeded', 'Failed', 'Pending'],
    active_tab: 'logs', // Default active tab
    tabs: [
      { label: 'Instant Verify Logs', value: 'logs' },
      { label: 'Service Response', value: 'responses' }
    ],
    filters: {
      service_id: '',
      verification_status: '',
      start_date: '',
      end_date: ''
    },
    response_filters: {
      service_id: '',
      verification_status: '',
      start_date: '',
      end_date: ''
    },
    api_responses: [],
    responses_current_page: 1,
    responses_items_per_page: 10,
    responses_total_pages: 1,
    response_accordion_states: {},
    columns: [
      { label: "Service Name", key: "service_name" },
      { label: "Time Requested", key: "date_created" },
      { label: "Verification Status", key: "verification_status" },
      { label: "API Status Result", key: "api_result_status" },
    ]
  }),

  computed: {
    client_id() {
      return this.$store.getters.get_client_id;
    },
  },

  methods: {
    // Data normalization methods
    extractValue(data, paths) {
      if (!data || !paths || !Array.isArray(paths)) {
        return null;
      }

      for (const path of paths) {
        const value = this.getValueByPath(data, path);
        if (value !== null && value !== undefined && value !== '') {
          return value;
        }
      }
      return null;
    },

    getValueByPath(obj, path) {
      if (!obj || !path) return null;
      
      return path.split('.').reduce((current, key) => {
        if (current === null || current === undefined) return null;
        
        // Handle array access like 'data[0]' or 'Results[0]'
        const arrayMatch = key.match(/^(\w+)\[(\d+)\]$/);
        if (arrayMatch) {
          const [, arrayKey, index] = arrayMatch;
          const array = current[arrayKey];
          return Array.isArray(array) ? array[parseInt(index)] : null;
        }
        
        return current[key];
      }, obj);
    },

    extractServiceData(result) {
      const service = result?.service_name?.toLowerCase() || '';

      if (service.includes('rsa id verification')) {
        return this.extractRsaIdData(result);
      }
      
      if (service.includes('enterprise anti money laundering')) {
        return this.extractEnterpriseAmlData(result);
      }
      
      if (service.includes('anti money laundering')) {
        return this.extractAmlData(result);
      }
      
      if (service.includes('enterprise credit check')) {
        return this.extractEnterpriseCreditData(result);
      }
      
      if (service.includes('refugee verification')) {
        return this.extractRefugeeData(result);
      }
      
      if (service.includes('address match')) {
        return this.extractAddressMatchData(result);
      }
      
      if (service.includes('trust name search')) {
        return this.extractTrustSearchData(result);
      }
      
      if (service.includes('cipc verification')) {
        return this.extractCipcData(result);
      }
      
      if (service.includes('director screening')) {
        return this.extractDirectorScreeningData();
      }
      
      if (service.includes('enterprise kyb')) {
        return this.extractEnterpriseKybData(result);
      }
      
      if (service.includes('income estimator') || service.includes('income predictor')) {
        return this.extractIncomeServiceData(result);
      }
      
      if (service.includes('bank account') && (service.includes('individual') || service.includes('company'))) {
        return this.extractBankAccountData(result);
      }

      // Default fallback
      return {
        service_type: 'unknown'
      };
    },

    extractRsaIdData(result) {
      const idPaths = [
        'data.response.IdNumber',
        'data.response.response.IdNumber',
        'data.IdNumber',
        'response.response.IdNumber',
        'response.IdNumber',
        'data.RequestData.id_number',
        'data.request_data.id_number',
        'request_data.id_number'
      ];

      return {
        id: this.extractValue(result, idPaths),
        service_type: 'rsa_id_verification'
      };
    },

    extractEnterpriseAmlData(result) {
      const namePaths = [
        'data.RequestData.EnterpriseName',
        'data.request_data.EnterpriseName',
        'request_data.EnterpriseName',
        'data.response.RequestData.EnterpriseName',
        'response.RequestData.EnterpriseName',
        'data.EnterpriseName',
        'EnterpriseName'
      ];

      return {
        enterprise_name: this.extractValue(result, namePaths),
        service_type: 'enterprise_aml'
      };
    },

    extractAmlData(result) {
      const firstNamePaths = [
        'response.RequestData.FirstName',
        'data.response.RequestData.FirstName',
        'data.RequestData.FirstName',
        'RequestData.FirstName',
        'request_data.FirstName',
        'data.request_data.FirstName'
      ];

      const lastNamePaths = [
        'response.RequestData.LastName',
        'data.response.RequestData.LastName',
        'data.RequestData.LastName',
        'RequestData.LastName',
        'request_data.LastName',
        'data.request_data.LastName'
      ];

      return {
        first_name: this.extractValue(result, firstNamePaths),
        last_name: this.extractValue(result, lastNamePaths),
        service_type: 'aml'
      };
    },

    extractEnterpriseCreditData(result) {
      const regNumberPaths = [
        'data.Header.RegistrationNumber',
        'data.CompanySearch.RegNo',
        'data.CV4BCB01.RegNumber',
        'data.CompanySearch.RegistrationNumber',
        'data.response.Header.RegistrationNumber',
        'data.response.CompanySearch.RegNo',
        'data.response.CV4BCB01.RegNumber',
        'response.Header.RegistrationNumber',
        'response.CompanySearch.RegNo',
        'response.CV4BCB01.RegNumber',
        'data.RequestData.registration_number',
        'data.request_data.registration_number',
        'request_data.registration_number'
      ];

      return {
        registration_number: this.extractValue(result, regNumberPaths),
        service_type: 'enterprise_credit'
      };
    },

    extractRefugeeData(result) {
      const refugeeNumberPaths = [
        'data.GoldenSource.Results[0].RefugeeIDNumber',
        'data.GoldenSource.Results[0].NIISFileNumber',
        'data.response.GoldenSource.Results[0].RefugeeIDNumber',
        'data.response.GoldenSource.Results[0].NIISFileNumber',
        'response.GoldenSource.Results[0].RefugeeIDNumber',
        'response.GoldenSource.Results[0].NIISFileNumber',
        'data.RequestData.refugee_number',
        'data.request_data.refugee_number',
        'request_data.refugee_number'
      ];

      return {
        refugee_number: this.extractValue(result, refugeeNumberPaths),
        service_type: 'refugee_verification'
      };
    },

    extractAddressMatchData(result) {
      // Special handling for array-based data
      let id = null;
      
      if (Array.isArray(result.data)) {
        const idItem = result.data.find(item => 
          item.label === 'IDNo' || item.label === 'IdNo' || item.label === 'id_no'
        );
        id = idItem?.value;
      }

      if (!id) {
        const idPaths = [
          'data.IDNo',
          'data.IdNo',
          'data.response.IDNo',
          'data.response.IdNo',
          'data.response.IdNumber',
          'response.IDNo',
          'response.IdNo',
          'response.IdNumber',
          'data.IdNumber',
          'IdNo',
          'IDNo',
          'data.RequestData.id_number',
          'data.request_data.id_number',
          'request_data.id_number'
        ];
        id = this.extractValue(result, idPaths);
      }

      return {
        id: id,
        service_type: 'address_match'
      };
    },

    extractTrustSearchData(result) {
      let trustName = null;

      // Handle the specific trust search response format
      let trustResults = null;
      
      // Try to get the results array from different possible locations
      if (result.response?.result) {
        try {
          trustResults = typeof result.response.result === 'string' 
            ? JSON.parse(result.response.result) 
            : result.response.result;
        } catch (e) {
          // Parsing failed, continue with other methods
        }
      }
      
      if (!trustResults && result.data?.result) {
        try {
          trustResults = typeof result.data.result === 'string' 
            ? JSON.parse(result.data.result) 
            : result.data.result;
        } catch (e) {
          // Parsing failed, continue with other methods
        }
      }

      // Check if data is already the parsed array
      if (!trustResults && Array.isArray(result.data)) {
        trustResults = result.data;
      }

      if (!trustResults && Array.isArray(result.response)) {
        trustResults = result.response;
      }

      // If we have trust results array, get the first trust name
      if (Array.isArray(trustResults) && trustResults.length > 0) {
        const firstTrust = trustResults[0];
        trustName = firstTrust.InputTrustName || firstTrust.TRNName || firstTrust.TrustName;
      }

      // Handle legacy array-based data
      if (!trustName && Array.isArray(result.data) && result.data[0]?.TrustName) {
        trustName = result.data[0].TrustName;
      }

      // Fallback to path-based extraction
      if (!trustName) {
        const trustNamePaths = [
          'data.response.TrustName',
          'data.response.InputTrustName',
          'data.response.TRNName',
          'response.TrustName',
          'response.InputTrustName',
          'response.TRNName',
          'data.TrustName',
          'data.InputTrustName',
          'data.TRNName',
          'data[0].TrustName',
          'data[0].InputTrustName',
          'data[0].TRNName',
          'data.RequestData.trust_name',
          'data.RequestData.InputTrustName',
          'data.RequestData.TRNName',
          'data.request_data.trust_name',
          'data.request_data.InputTrustName',
          'data.request_data.TRNName',
          'request_data.trust_name',
          'request_data.InputTrustName',
          'request_data.TRNName',
          'InputTrustName',
          'TRNName'
        ];
        
        trustName = this.extractValue(result, trustNamePaths);
      }

      return {
        trust_name: trustName,
        service_type: 'trust_search'
      };
    },

    extractCipcData(result) {
      const companyNamePaths = [
        'data.CompanySearch.Name',
        'data.response.CompanySearch.Name',
        'response.CompanySearch.Name',
        'data.RequestData.company_name',
        'data.request_data.company_name',
        'request_data.company_name'
      ];

      const regNumberPaths = [
        'data.CompanySearch.RegNo',
        'data.response.CompanySearch.RegNo',
        'response.CompanySearch.RegNo',
        'data.RequestData.registration_number',
        'data.request_data.registration_number',
        'request_data.registration_number'
      ];

      return {
        company_name: this.extractValue(result, companyNamePaths),
        registration_number: this.extractValue(result, regNumberPaths),
        service_type: 'cipc_verification'
      };
    },

    extractDirectorScreeningData() {
      return {
        service_type: 'director_screening'
      };
    },

    extractEnterpriseKybData(result) {
      const companyNamePaths = [
        'data.CompanySearch.Name',
        'data.response.CompanySearch.Name',
        'response.CompanySearch.Name',
        'data.RequestData.company_name',
        'data.request_data.company_name',
        'request_data.company_name'
      ];

      return {
        company_name: this.extractValue(result, companyNamePaths),
        service_type: 'enterprise_kyb'
      };
    },

    extractIncomeServiceData(result) {
      const service = result?.service_name?.toLowerCase() || '';
      return {
        service_type: service.includes('estimator') ? 'income_estimator' : 'income_predictor'
      };
    },

    extractBankAccountData(result) {
      return {
        service_type: result?.service_name?.toLowerCase().includes('company') 
          ? 'bank_account_company' 
          : 'bank_account_individual'
      };
    },

    get_result_title(result) {
      const data = this.extractServiceData(result);
      const date = this.format_date(result.date_created || result.date);

      switch (data.service_type) {
        case 'rsa_id_verification':
          return data.id 
            ? `Results for: ${data.id} on ${date}`
            : `Results on ${date}`;

        case 'enterprise_aml':
          return data.enterprise_name 
            ? `Results for: ${data.enterprise_name} on ${date}`
            : `Results on ${date}`;

        case 'aml':
          return data.first_name || data.last_name
            ? `Results for: ${data.first_name || ''} ${data.last_name || ''} on ${date}`.trim()
            : `Results on ${date}`;

        case 'enterprise_credit':
          return data.registration_number 
            ? `Results for: ${data.registration_number} on ${date}`
            : `Results on ${date}`;

        case 'refugee_verification':
          return data.refugee_number 
            ? `Results for: ${data.refugee_number} on ${date}`
            : `Results on ${date}`;

        case 'address_match':
          return data.id 
            ? `Results for: ${data.id} on ${date}`
            : `Results on ${date}`;

        case 'trust_search':
          return data.trust_name 
            ? `Results for: ${data.trust_name} on ${date}`
            : `Results on ${date}`;

        case 'cipc_verification':
          return data.company_name || data.registration_number
            ? `Results for: ${data.company_name || data.registration_number} on ${date}`
            : `Results on ${date}`;

        case 'director_screening':
          return `Director Screening Results: ${date}`;

        case 'enterprise_kyb':
          return data.company_name 
            ? `Results for: ${data.company_name} on ${date}`
            : `Results on ${date}`;

        case 'income_estimator':
          return `Income Estimation: ${date}`;

        case 'income_predictor':
          return `Income Prediction: ${date}`;

        case 'bank_account_individual':
        case 'bank_account_company':
          return `Verification Result: ${date}`;

        default:
          return `Verification Result: ${date}`;
      }
    },
    format_date(date) {
      return format_date(date);
    },
    get_progress_bar_color(success_rate) {
      if (success_rate >= 90) return "green";
      if (success_rate >= 80) return "orange";
      return "red";
    },
    get_badge_color(success_rate) {
      if (success_rate >= 90) return "#16a34a";
      if (success_rate >= 80) return "#ea580c";
      return "#dc2626";
    },
    get_logs(page = 1) {
      const TOKEN = localStorage.getItem("token");
      return new Promise((resolve, reject) => {
        let url = `${API_BASE_URL}/verify_api/verify_admin_api/get_log_information/?client_id=${this.client_id}&page=${page}&page_size=10`;
        
        // Add filter parameters
        if (this.filters.service_id) {
          url += `&service_id=${encodeURIComponent(this.filters.service_id)}`;
        }
        if (this.filters.verification_status) {
          url += `&verification_status=${encodeURIComponent(this.filters.verification_status)}`;
        }
        if (this.filters.start_date) {
          url += `&start_date=${encodeURIComponent(this.filters.start_date)}`;
        }
        if (this.filters.end_date) {
          url += `&end_date=${encodeURIComponent(this.filters.end_date)}`;
        }
        
        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (data) => {
            if (data.status === 'success') {
              resolve({
                count: data.data.count,
                results: data.data.results,
              });
            } else {
              reject(new Error(data.message || 'Failed to fetch logs'));
            }
          },
          error: (xhr, status, error) => {
            reject(error);
          }
        });
      });
    },
    fetch_analytical_data() {
      this.loading = true;
      return new Promise((resolve, reject) => {
        const TOKEN = localStorage.getItem("token");
        const url = `${API_BASE_URL}/verify_api/verify_admin_api/get_log_analytical_data/?client_id=${this.client_id}`;
        
        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (data) => {
            this.loading = false;
            if (data.status === 'success') {
              this.analytical_data = data.data;
              this.update_services_list();
              this.update_top_services();
              resolve(data.data);
            } else {
              reject(new Error(data.message || 'Failed to fetch analytical data'));
            }
          },
          error: (xhr, status, error) => {
            this.loading = false;
            reject(error);
          },
        });
      });
    },
    fetch_api_response_analytical_data() {
      return new Promise((resolve, reject) => {
        const TOKEN = localStorage.getItem("token");
        const url = `${API_BASE_URL}/verify_api/verify_admin_api/get_api_response_analytical_data/?client_id=${this.client_id}`;

        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (data) => {
            if (data.status === 'success') {
              this.response_services_list = data.data.services_list || [];
              this.response_service_id_map = data.data.service_id_map || {};
              resolve(data.data);
            } else {
              reject(new Error(data.message || 'Failed to fetch API response analytical data'));
            }
          },
          error: (xhr, status, error) => {
            reject(error);
          },
        });
      });
    },
    update_services_list() {
      if (this.analytical_data && this.analytical_data.services_list) {
        this.services_list = this.analytical_data.services_list;
        this.service_id_map = this.analytical_data.service_id_map || {};
      }
    },
    update_top_services() {
      if (this.analytical_data && this.analytical_data.top_5_services_last_30_days) {
        this.top_services = this.analytical_data.top_5_services_last_30_days.map(service => ({
          service_name: service.service_name,
          success_rate: Math.round(service.success_rate * 10) / 10,
          total_requests: service.total_requests,
          successful_requests: service.successful_requests
        }));
      } else {
        // Fallback to old logic if new data is not available
        if (this.analytical_data && this.analytical_data.service_stats) {
          const service_stats = this.analytical_data.service_stats;
          this.top_services = Object.entries(service_stats)
            .map(([service_name, stats]) => {
              const success_rate = stats.total_requests > 0 
                ? (stats.successful_requests / stats.total_requests * 100) 
                : 0;
              return {
                service_name: service_name,
                success_rate: Math.round(success_rate * 10) / 10,
                total_requests: stats.total_requests,
                successful_requests: stats.successful_requests
              };
            })
            .sort((a, b) => b.total_requests - a.total_requests)
            .slice(0, 5);
        }
      }
    },
    mounted_graphs() {
      if (this.analytical_data && this.analytical_data.service_stats) {
        const service_stats = this.analytical_data.service_stats;
        const services = Object.keys(service_stats);
        const success_data = services.map(service => service_stats[service].successful_requests);
        const failure_data = services.map(service => service_stats[service].total_requests - service_stats[service].successful_requests);
        const options = {
          series: [
            {
              name: 'Success',
              data: success_data
            },
            {
              name: 'Failure',
              data: failure_data
            }
          ],
          chart: {
            type: 'bar',
            height: 400,
            stacked: true,
          },
          plotOptions: {
            bar: {
              horizontal: true,
            },
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            categories: services,
          },
          yaxis: {
            title: {
              text: 'Number of Requests'
            }
          },
          tooltip: {
            y: {
              formatter: function (val, opts) {
                const total = opts.series[0][opts.dataPointIndex] + opts.series[1][opts.dataPointIndex];
                const percentage = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
                return val + ' requests (' + percentage + '%)';
              }
            }
          },
          colors: ['#16a34a', '#dc2626'],
          title: {
            text: 'Service Usage Breakdown: Success vs Failure',
            align: 'center'
          }
        };
        const chart = new window.ApexCharts(document.querySelector('.log-graphs'), options);
        chart.render();
      } else {
        console.error('No analytical data available for chart rendering');
      }
    },
    apply_filters() {
      if (this.$refs.logsTable) {
        this.$refs.logsTable.reload();
      }
    },
    reset_filters() {
      this.filters = {
        service_id: '',
        verification_status: '',
        start_date: '',
        end_date: ''
      };
      this.apply_filters();
    },
    // Tab handling methods
    on_tab_changed(tab_value) {
      this.active_tab = tab_value;
      if (tab_value === 'responses') {
        this.fetch_api_response_analytical_data()
          .then(() => {
            this.load_api_responses();
          })
          .catch((error) => {
            console.error('Error fetching API response analytical data:', error);
            this.load_api_responses(); // Still load responses even if analytical data fails
          });
      }
    },
    // Response methods
    get_api_responses(page = 1) {
      const TOKEN = localStorage.getItem("token");
      return new Promise((resolve, reject) => {
        let url = `${API_BASE_URL}/verify_api/verify_admin_api/get_log_api_responses/?client_id=${this.client_id}&page=${page}&page_size=${this.responses_items_per_page}`;

        // Add filter parameters
        if (this.response_filters.service_id) {
          url += `&service_id=${encodeURIComponent(this.response_filters.service_id)}`;
        }
        if (this.response_filters.start_date) {
          url += `&start_date=${encodeURIComponent(this.response_filters.start_date)}`;
        }
        if (this.response_filters.end_date) {
          url += `&end_date=${encodeURIComponent(this.response_filters.end_date)}`;
        }

        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (data) => {
            if (data.status === 'success') {
              // Process the responses similar to service_utilize
              const processed_responses = this.process_api_responses(data.data.results);
              this.api_responses = processed_responses;
              this.responses_total_pages = Math.ceil(data.data.count / this.responses_items_per_page);
              resolve({
                count: data.data.count,
                results: processed_responses,
              });
            } else {
              reject(new Error(data.message || 'Failed to fetch API responses'));
            }
          },
          error: (xhr, status, error) => {
            reject(error);
          }
        });
      });
    },
    
    load_api_responses() {
      this.get_api_responses(this.responses_current_page).then(() => {
        // Data loaded successfully
      }).catch((error) => {
        console.error('Error loading API responses:', error);
      });
    },
    apply_response_filters() {
      this.responses_current_page = 1;
      this.load_api_responses();
    },
    reset_response_filters() {
      this.response_filters = {
        service_id: '',
        start_date: '',
        end_date: ''
      };
      this.responses_current_page = 1;
      this.load_api_responses();
    },
    
    // Response accordion methods
    toggle_response_accordion(id) {
      const collapse_element = document.getElementById(`response-collapse-${id}`);
      if (collapse_element) {
        if (collapse_element.classList.contains('show')) {
          collapse_element.classList.remove('show');
          // Update accordion state
          this.response_accordion_states = {
            ...this.response_accordion_states,
            [id]: false
          };
        } else {
          // Close all other accordions first
          Object.keys(this.response_accordion_states).forEach(key => {
            if (key != id && this.response_accordion_states[key]) {
              const other_element = document.getElementById(`response-collapse-${key}`);
              if (other_element) {
                other_element.classList.remove('show');
              }
            }
          });

          // Reset all states to false, then set current to true
          const new_states = {};
          Object.keys(this.response_accordion_states).forEach(key => {
            new_states[key] = false;
          });
          new_states[id] = true;

          this.response_accordion_states = new_states;
          collapse_element.classList.add('show');

          // Scroll to the opened accordion after a brief delay
          setTimeout(() => {
            const accordion_header = document.getElementById(`response-heading-${id}`);
            if (accordion_header) {
              accordion_header.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }, 300); // Increased delay to allow accordion to fully open
        }
      }
    },
    
    is_response_accordion_open(id) {
      return this.response_accordion_states[id] || false;
    },
    
    responses_previous_page() {
      if (this.responses_current_page > 1) {
        this.responses_current_page--;
        this.load_api_responses();
      }
    },
    
    responses_next_page() {
      if (this.responses_current_page < this.responses_total_pages) {
        this.responses_current_page++;
        this.load_api_responses();
      }
    },
    format_response(response) {
      if (typeof response === 'string') {
        try {
          return JSON.stringify(JSON.parse(response), null, 2);
        } catch {
          return response;
        }
      }
      return JSON.stringify(response, null, 2);
    },
    process_api_responses(responses) {
      return responses.map(response => {
        let processed_data = response.response;

        // Try to parse JSON response if it's a string
        if (typeof processed_data === 'string') {
          try {
            processed_data = JSON.parse(processed_data);
          } catch (e) {
            // If parsing fails, keep as string
            processed_data = response.response;
          }
        }

        let processed_response = null;

        // Process the data based on service type (similar to service_utilize)
        if (response.service_name?.toLowerCase().includes('anti money laundering') ||
            response.service_name?.toLowerCase().includes('enterprise anti money laundering')) {
          processed_response = this.process_aml_response(processed_data);
          processed_response = this.preprocess_data(processed_response);
        } else if (response.service_name?.toLowerCase().includes('enterprise credit check') ||
                   response.service_name?.toLowerCase().includes('refugee verification') ||
                   response.service_name?.toLowerCase().includes('rsa id verification') ||
                   response.service_name?.toLowerCase().includes('trust search') ||
                   response.service_name?.toLowerCase().includes('cipc verification') ||
                   response.service_name?.toLowerCase().includes('bank account individual verification') ||
                   response.service_name?.toLowerCase().includes('bank account company verification')) {
          // For these services, preprocess the data
          try {
            processed_response = this.preprocess_data(processed_data);
          } catch (error) {
            console.error('Error preprocessing data:', error);
            processed_response = null;
          }
        } else {
          // For other services, preprocess the data
          try {
            processed_response = this.preprocess_data(processed_data);
          } catch (error) {
            console.error('Error preprocessing data:', error);
            processed_response = null;
          }
        }

        return {
          ...response,
          processed_response: processed_response
        };
      });
    },
    process_aml_response(response) {
      if (!response) return {};

      let aml_result = response?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DSWatchlistVerification;
      if (!aml_result) {
        aml_result = response?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DRWatchlistVerification;
      }
      const request_data = response?.RequestData || {};

      if (!aml_result) return { RequestData: request_data };

      return {
        RequestData: request_data,
        NmResults: aml_result?.NmResults || [],
        PepResults: aml_result?.PepResults || [],
        WlsResults: aml_result?.WlsResults || []
      };
    },
    preprocess_data(data, parent_key = '') {
      let result = [];

      let format_key = (key, parent) => (parent ? `${parent}.${key}` : key);

      Object.entries(data).forEach(([key, value]) => {
        let current_key = format_key(key, parent_key);

        // Special handling for DisplayText
        if (key === 'DisplayText' && typeof value === 'string' && value.trim() !== '') {
          result.push({
            type: 'heading',
            key: current_key,
            label: value
          });
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              result.push(...this.preprocess_data(item, `${current_key}[${index}]`));
            } else {
              const processed_value = (item === null || item === undefined || item === '') ? 'N/A' : item;
              result.push({
                type: 'input',
                key: `${current_key}[${index}]`,
                label: `${key} [${index}]`,
                value: processed_value,
              });
            }
          });
        } else if (typeof value === 'object' && value !== null) {
          result.push({
            type: 'heading',
            key: current_key,
            label: key,
          });
          result.push(...this.preprocess_data(value, current_key));
        } else if (value !== null && value !== '') {
          result.push({
            type: 'input',
            key: current_key,
            label: key,
            value: value,
          });
        }
      });
      return result;
    },
    format_label(key) {
      if (typeof key !== 'string' || !key.trim()) return '';

      return key
        .replace(/[_-]/g, ' ')
        .replace(/[^a-zA-Z0-9]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },
    
    // Methods from service_utilize.js for processing AML and other responses
    aml_results(result) {
      let aml_result = result?.response?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DSWatchlistVerification;
      if (!aml_result) {
        aml_result = result?.response?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DRWatchlistVerification;
      }
      var request_data = result?.response?.RequestData || {};
      if (!aml_result) return null;

      return {
        RequestData: request_data,
        NmResults: aml_result?.NmResults || [],
        PepResults: aml_result?.PepResults || [],
        WlsResults: aml_result?.WlsResults || []
      };
    },
    
    response_info_aml(result) {
      const response_info_result = result?.response?.ResponseInfo;
      if (!response_info_result) return null;
      return response_info_result;
    },
    
    date_scored(result) {
      const date_scored_result = result?.response?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DRWatchlistVerification;
      if (!date_scored_result) return null;
      return date_scored_result?.TimeOfRequest || '';
    },
    
    get_result_data(response) {
      // Process response data similar to service_utilize
      if (response.service_name?.toLowerCase().includes('enterprise anti money laundering') ||
          response.service_name?.toLowerCase().includes('anti money laundering')) {
        // For AML services, use the AML processing
        return this.aml_results(response);
      } else if (
        response.service_name?.toLowerCase().includes('enterprise credit check') ||
        response.service_name?.toLowerCase().includes('refugee verification') ||
        response.service_name?.toLowerCase().includes('rsa id verification') ||
        response.service_name?.toLowerCase().includes('trust search') ||
        response.service_name?.toLowerCase().includes('cipc verification') ||
        response.service_name?.toLowerCase().includes('bank account individual verification') ||
        response.service_name?.toLowerCase().includes('bank account company verification')
      ) {
        // For these services, return the raw response data
        let processed_response = response.response;
        if (typeof processed_response === 'string') {
          try {
            processed_response = JSON.parse(processed_response);
          } catch (e) {
            processed_response = response.response;
          }
        }
        return processed_response;
      }
      
      // For other services, preprocess the data
      let processed_response = response.response;
      if (typeof processed_response === 'string') {
        try {
          processed_response = JSON.parse(processed_response);
        } catch (e) {
          processed_response = response.response;
        }
      }
      
      try {
        return this.preprocess_data(processed_response);
      } catch (error) {
        console.error('Error preprocessing data:', error);
        return null;
      }
    },
  },
  async mounted() {
    const load_script = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          // already injected
          const check = () => {
            // ← wait for ApexCharts, not jspdf
            if (src.includes("apexcharts") && window.ApexCharts) {
              resolve();
            } else {
              setTimeout(check, 50);
            }
          };
          return check();
        }
        const s = document.createElement("script");
        s.src = src;
        s.async = false;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

    // only loading ApexCharts here
    await load_script("https://cdn.jsdelivr.net/npm/apexcharts");
    
    if (!validateToken(this.$router)) {
        return;
    }
    
    // Fetch analytical data first
    try {
      await this.fetch_analytical_data();
      // CustomTable will load data automatically on mount
      // now guaranteed window.ApexCharts → draw
      this.mounted_graphs();
    } catch (error) {
      console.error('Error fetching analytical data:', error);
      // Still try to load logs and graphs with empty data
      // CustomTable will handle loading logs
      this.mounted_graphs();
    }
  },
  onBeforeUnmount() {
    const script = document.querySelector(`script[src="https://cdn.jsdelivr.net/npm/apexcharts"]`);
    if (script) {
      document.head.removeChild(script);
    }
  }
};
